import { EventTypes } from 'types/events'
import { useUniversalPayload } from './useUniversalPayload'
import { FormattedCaller } from 'components/fields/Submit'
import { FormState } from 'final-form'
import { TowExchange } from 'types/callstore'
import { useTranslation } from 'react-i18next'
import { useServiceProxy } from 'hooks/kong'
import { useDispatch, useSelector } from 'react-redux'
import { selectCurrentUser, selectFormsState, selectTicket } from 'redux/appStore'
import useUpdateAssignment from 'hooks/assignment/useUpdateAssignment'
import { useCreateServiceEvent } from 'hooks/events'
import { useTowExchangeEmail } from 'hooks/aaa'
import useUpdateTicketCharges from 'hooks/tickets/useUpdateTicketCharges'
import { useFleetVehicleVEMNote } from 'hooks/kong/useFleetVehicleVEMNote'
import { BranchInfoFragment } from 'types/branch'
import { createExchangeFromTowEvent } from 'utils/exchange'
import { setCurrentEvent } from 'redux/currentEvent/currentEventSlice'
import { allStateData } from 'types/allstate'
import { ProviderNames } from 'hooks/dispatch/utils'

export const useCreateUniversalService = (eventType: EventTypes) => {
  const { t } = useTranslation()
  const generateUniversalPayload = useUniversalPayload()
  const ticket = useSelector(selectTicket)
  const currentUser = useSelector(selectCurrentUser)
  const { additionalService } = useSelector(selectFormsState) as any
  const updateAssignment = useUpdateAssignment()
  const serviceProxy = useServiceProxy()
  const dispatch = useDispatch()
  const createServiceEvent = useCreateServiceEvent({ eventType })
  const sendTowExchangeEmail = useTowExchangeEmail()
  const updateTicketCharges = useUpdateTicketCharges()
  const createVEMNote = useFleetVehicleVEMNote()

  return async function (state: FormState<any>, caller: FormattedCaller, provider: string) {
    const service = state.values

    try {
      const universalPayload = await generateUniversalPayload(service, eventType)
      //// Dispatch API Request ////
      let data: allStateData | undefined

      // Validate Allstate tow/exchange payload before dispatching and log missing vehicle destination cases
      const isValid = await validateAllstateTowDestination({
        provider,
        eventType,
        serviceLocations: state.values?.serviceLocations,
        universalPayload,
        ticketNumber: ticket?.ticketNumber,
        serviceProxy,
      })

      if (!isValid) {
        return t('error.allstate')
      }

      if (provider === ProviderNames.ALLSTATE) {
        const response = await serviceProxy<allStateData>(
          'post',
          '/serviceproxy/allstate/api/v1/create/em/dispatch',
          {},
          universalPayload,
        )
        data = response.data
      }

      if (data) {
        //// If Exchange ////
        if (eventType === EventTypes.EXCHANGE) {
          const pickupBranch = ticket.pickupInfo.branch as BranchInfoFragment
          const towExchangeValues = state.values as TowExchange
          const exchangeEvent = await createServiceEvent({
            ...createExchangeFromTowEvent(towExchangeValues),
            eventType: EventTypes.EXCHANGE,
            rentalBrand: ticket.brandId,
            rentalGPBR: pickupBranch.groupBranchNumber,
            rentalStationId: pickupBranch.stationId,
            ticketNumber: ticket.ticketNumber,
            vinLast8: ticket.vehicle!.vinLast8,
            agentEID: currentUser?.nameID,
          })

          await sendTowExchangeEmail(state.values as TowExchange, caller)
          dispatch(
            setCurrentEvent({
              eventType: EventTypes.EXCHANGE,
              newEvent: exchangeEvent.data,
            }),
          )
        }

        //// Update Ticket misc Charges ////
        await updateTicketCharges(eventType, ProviderNames.ALLSTATE)

        //// Update Assignment only for Tow ////
        if (service.towLocation) {
          await updateAssignment(state.values)
        }

        //// Create VEM Note ////
        await createVEMNote(eventType ?? EventTypes.TOW)

        //// Create service event in DB ////
        const event = await createServiceEvent({
          eventType: service.towLocation ? EventTypes.TOW : eventType,
          ...service,
          provider: provider,
          externalId: universalPayload.externalId,
          dispatchId: data.dispatchId || 'N/A',
          customerOptIn: caller.optIn || false,
        })

        //// Dispatch to Redux ////
        dispatch(
          setCurrentEvent({
            eventType: service.towLocation ? EventTypes.TOW : eventType,
            newEvent: event.data,
          }),
        )
      }
    } catch (error) {
      console.error(error)
      return t(`error.${provider.toLowerCase()}`) || t(`error.universal`)
    }
  }
}

const validateAllstateTowDestination = async ({
  provider,
  eventType,
  serviceLocations,
  universalPayload,
  ticketNumber,
  serviceProxy,
}: {
  provider: string
  eventType: EventTypes
  serviceLocations?: any
  universalPayload: any
  ticketNumber?: string
  serviceProxy: any
}): Promise<boolean> => {
  const isMissingTowDestination =
    provider === ProviderNames.ALLSTATE &&
    (eventType === EventTypes.TOW || eventType === EventTypes.EXCHANGE) &&
    !universalPayload?.service?.serviceLocations?.vehicleDestination

  if (!isMissingTowDestination) {
    return true
  }

  const alertPayload = {
    alertType: 'ALLSTATE_MISSING_TOW_DESTINATION',
    sendEmail: false,
    message: 'Allstate dispatch blocked. vehicleDestination missing during payload validation.',
    ticketNumber,
    provider,
    eventType,
    // Compare form source data vs generated payload to identify where destination was lost
    originalVehicleDestinationLocation: serviceLocations?.vehicleDestinationLocation,
    payloadVehicleDestination: universalPayload?.service?.serviceLocations?.vehicleDestination,
    serviceLocations,
    payloadServiceLocations: universalPayload?.service?.serviceLocations,
  }

  try {
    await serviceProxy('post', '/serviceproxy/alert', {}, alertPayload)
  } catch (error) {
    console.error('Failed to create ALLSTATE_MISSING_TOW_DESTINATION alert', error)
  }

  return false
}
